import React from 'react';
import type { ReactElement } from 'react';
import { Flex } from '@zillow/constellation';
import type { CardProps } from '@zillow/constellation';

import * as S from './styles';
import numberUtils from 'app/shared/utils/numberUtils';
import DataCard from 'app/shared/components/DataCard';
import ResponsiveHeading from 'app/shared/StyledComponents/ResponsiveText';

interface MarketSummaryProps {
  heading?: string | ReactElement;
  market: string;
  filterDescription: string;
  medianRent: number | null;
  monthlyChange: number | null;
  yearlyChange: number | null;
  avgDaysOnMarket: number | null;
  availableRentals: number | null;
  isSmallScreen: boolean;
}

const MarketSummary: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<MarketSummaryProps & CardProps>>
> = (props) => {
  const {
    heading,
    market,
    filterDescription,
    medianRent,
    monthlyChange,
    yearlyChange,
    avgDaysOnMarket,
    availableRentals,
    isSmallScreen,
    // @ts-ignore
    appearance,
    cardElevation,
  } = props;

  return (
    <DataCard
      heading={heading ? heading : `Rental market summary`}
      copyText={
        medianRent ? (
          <>
            The average rent for {filterDescription} in {market} is{' '}
            <strong>{numberUtils.asCurrency(medianRent)}.</strong>
          </>
        ) : (
          ''
        )
      }
      source="Zillow Rentals data"
      useFullScreen={true}
      // @ts-ignore
      appearance={appearance}
      cardElevation={cardElevation}
    >
      <Flex
        display="flex"
        flexWrap="wrap"
        marginBottom="xl"
        marginX={isSmallScreen ? undefined : 'md'}
        justifyContent="space-between"
        // @ts-ignore
        gap="md"
      >
        {medianRent !== null && (
          <S.StatContainer isSmallScreen={isSmallScreen}>
            <S.StatName fontType="bodySmall">Average rent</S.StatName>
            <ResponsiveHeading level="2" as="p" fontFamily="serif" fontType="h2">
              {numberUtils.asCurrency(medianRent)}
            </ResponsiveHeading>
          </S.StatContainer>
        )}
        {monthlyChange !== null && (
          <S.StatContainer isSmallScreen={isSmallScreen}>
            <S.StatName fontType="bodySmall">{isSmallScreen ? 'MoM' : 'Month-over-month'} change</S.StatName>
            <ResponsiveHeading level="2" as="p" fontFamily="serif" fontType="h2">
              {monthlyChange > 0 && '+'}
              {numberUtils.asCurrency(monthlyChange)}
            </ResponsiveHeading>
          </S.StatContainer>
        )}
        {yearlyChange !== null && (
          <S.StatContainer isSmallScreen={isSmallScreen}>
            <S.StatName fontType="bodySmall">{isSmallScreen ? 'YoY' : 'Year-over-year'} change</S.StatName>
            <ResponsiveHeading level="2" as="p" fontFamily="serif" fontType="h2">
              {yearlyChange > 0 && '+'}
              {numberUtils.asCurrency(yearlyChange)}
            </ResponsiveHeading>
          </S.StatContainer>
        )}
        {avgDaysOnMarket !== null && (
          <S.StatContainer isSmallScreen={isSmallScreen}>
            <S.StatName fontType="bodySmall">Avg. Days On Market</S.StatName>
            <ResponsiveHeading level="2" as="p" fontFamily="serif" fontType="h2">
              {numberUtils.fullNumber(Math.round(avgDaysOnMarket))}
            </ResponsiveHeading>
          </S.StatContainer>
        )}
        {availableRentals !== null && (
          <S.StatContainer isSmallScreen={isSmallScreen}>
            <S.StatName fontType="bodySmall">Available rentals</S.StatName>
            <ResponsiveHeading level="2" as="p" fontFamily="serif" fontType="h2">
              {numberUtils.fullNumber(availableRentals)}
            </ResponsiveHeading>
          </S.StatContainer>
        )}
      </Flex>
    </DataCard>
  );
};

export default MarketSummary;
